import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { DropdownPortal } from "../common/DatePickers";
import {
  FaCalendarAlt,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaCheck,
  FaSearch,
  FaUser,
} from "react-icons/fa";
import {
  EmployeeListItem,
  EmployeeStats,
} from "../../store/allocation/employeeDashboardSlice";
import { overviewEmployeeGroup } from "../../utils/employeeRoles";
import {
  monthBounds,
  sameDayInMonth,
  weekContaining,
  weeksInMonth,
} from "../../utils/operationalCalendar";
import { DASHBOARD } from "./dashboardTheme";

type EmployeePickerItem = Pick<EmployeeStats, "emp_id" | "emp_name" | "role"> & {
  shift?: string;
  overview_group?: EmployeeListItem["overview_group"];
};

const pickerGroup = (emp: EmployeePickerItem | EmployeeListItem) =>
  emp.overview_group || overviewEmployeeGroup(emp.role);

export function formatDisplayDate(iso: string) {
  const [yyyy, mm, dd] = iso.split("-");
  if (!yyyy || !mm || !dd) return iso;
  return `${dd}-${mm}-${yyyy}`;
}

function parseIsoToDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function dateToIso(d: Date) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function useDismiss(
  open: boolean,
  onClose: () => void,
  panelRef?: RefObject<HTMLElement | null>,
) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target) || panelRef?.current?.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, panelRef]);

  return ref;
}

const roleColor = (role: string) => {
  const r = role.toLowerCase();
  if (r.includes("captain")) return DASHBOARD.darkTeal;
  if (r.includes("senior")) return DASHBOARD.purple;
  if (r.includes("designer")) return DASHBOARD.teal;
  if (r.includes("admin") || r.includes("crm")) return DASHBOARD.terracotta;
  return DASHBOARD.textMuted;
};

type Variant = "header" | "light";

function TriggerShell({
  variant,
  open,
  onClick,
  disabled,
  icon,
  label,
  value,
  className = "",
}: {
  variant: Variant;
  open: boolean;
  onClick: () => void;
  disabled?: boolean;
  icon: ReactNode;
  label: string;
  value: string;
  className?: string;
}) {
  const isHeader = variant === "header";

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 min-w-0 w-full text-left transition-all disabled:opacity-50 ${
        isHeader
          ? `bg-white/10 hover:bg-white/15 border ${open ? "border-white/40 ring-2 ring-white/20" : "border-white/10"}`
          : `bg-white border shadow-sm hover:border-[#93B9AD]/50 ${open ? "border-[#93B9AD] ring-2 ring-[#93B9AD]/20" : "border-[#E8E8E4]"}`
      } ${className}`}
    >
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
          isHeader ? "bg-white/10 text-white/90" : "text-white"
        }`}
        style={isHeader ? undefined : { backgroundColor: DASHBOARD.teal }}
      >
        {icon}
      </span>
      <span className="flex flex-col min-w-0 flex-1">
        <span
          className={`text-[10px] uppercase tracking-wider font-semibold ${
            isHeader ? "text-white/60" : "text-[#8A8A85]"
          }`}
        >
          {label}
        </span>
        <span
          className={`text-sm font-semibold truncate ${
            isHeader ? "text-white" : "text-[#4A4A48]"
          }`}
        >
          {value}
        </span>
      </span>
      <FaChevronDown
        className={`shrink-0 text-xs transition-transform ${open ? "rotate-180" : ""} ${
          isHeader ? "text-white/70" : "text-[#8A8A85]"
        }`}
      />
    </button>
  );
}

function DropdownPanel({
  children,
  className = "",
  width = "w-full min-w-[280px]",
}: {
  children: ReactNode;
  className?: string;
  width?: string;
}) {
  return (
    <div
      className={`absolute left-0 right-0 sm:right-auto z-[100] mt-2 ${width} rounded-2xl border border-[#E8E8E4] bg-white shadow-xl overflow-hidden ${className}`}
      style={{ boxShadow: "0 12px 40px rgba(0,0,0,0.12)" }}
    >
      {children}
    </div>
  );
}

export function CustomEmployeeSelect({
  team,
  value,
  onChange,
  disabled = false,
  placeholder = "Select employee…",
  allowAll = false,
  allLabel = "All employees",
  variant = "header",
  className = "",
  grouped = false,
}: {
  team: EmployeePickerItem[] | EmployeeListItem[];
  value: number | "";
  onChange: (empId: number | "") => void;
  disabled?: boolean;
  placeholder?: string;
  allowAll?: boolean;
  allLabel?: string;
  variant?: Variant;
  className?: string;
  grouped?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const close = useCallback(() => {
    setOpen(false);
    setSearch("");
  }, []);
  const ref = useDismiss(open, close);

  const selected = team.find((e) => e.emp_id === value);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matched = q
      ? team.filter(
          (e) =>
            e.emp_name.toLowerCase().includes(q) ||
            e.role.toLowerCase().includes(q) ||
            (e.shift || "").toLowerCase().includes(q)
        )
      : team;
    if (!grouped || q) return matched;
    return matched.filter((e) => pickerGroup(e) !== "other");
  }, [team, search, grouped]);

  const groupedRows = useMemo(() => {
    if (!grouped) return [{ label: null as string | null, rows: filtered }];
    const primary = filtered.filter((e) => pickerGroup(e) === "primary");
    const secondary = filtered.filter((e) => pickerGroup(e) === "secondary");
    const other = filtered.filter((e) => pickerGroup(e) === "other");
    return [
      { label: "Design team", rows: primary },
      { label: "CRM & Admin", rows: secondary },
      { label: "Other", rows: other },
    ].filter((section) => section.rows.length);
  }, [filtered, grouped]);

  const displayValue =
    value === "" && allowAll
      ? allLabel
      : selected
        ? selected.emp_name
        : placeholder;

  return (
    <div ref={ref} className={`relative min-w-0 ${className}`}>
      <TriggerShell
        variant={variant}
        open={open}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        icon={<FaUser className="text-xs" />}
        label="Employee"
        value={displayValue}
      />

      {open ? (
        <DropdownPanel width="w-full sm:w-[320px]">
          <div className="p-3 border-b border-[#E8E8E4] bg-[#FDFBF2]">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A85] text-xs" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search employees…"
                className="w-full rounded-xl border border-[#E8E8E4] bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-[#93B9AD]"
                autoFocus
              />
            </div>
          </div>
          <ul className="max-h-[280px] overflow-y-auto py-1">
            {allowAll ? (
              <li>
                <button
                  type="button"
                  onClick={() => {
                    onChange("");
                    close();
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                    value === "" ? "bg-[#345952]/8" : "hover:bg-[#FDFBF2]"
                  }`}
                >
                  <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white text-xs font-bold"
                    style={{ backgroundColor: DASHBOARD.header }}
                  >
                    *
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-[#4A4A48] truncate">
                      {allLabel}
                    </span>
                    <span className="block text-[11px] text-[#8A8A85] truncate">
                      Show every employee
                    </span>
                  </span>
                  {value === "" ? (
                    <FaCheck className="shrink-0 text-sm" style={{ color: DASHBOARD.header }} />
                  ) : null}
                </button>
              </li>
            ) : null}
            {filtered.length === 0 ? (
              <li className="px-4 py-6 text-center text-sm text-[#8A8A85]">No matches</li>
            ) : (
              groupedRows.map((section) => (
                <Fragment key={section.label || "all"}>
                  {section.label ? (
                    <li className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8A8A85]">
                      {section.label}
                    </li>
                  ) : null}
                  {section.rows.map((emp) => {
                    const active = emp.emp_id === value;
                    return (
                      <li key={emp.emp_id}>
                        <button
                          type="button"
                          onClick={() => {
                            onChange(emp.emp_id);
                            close();
                          }}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                            active ? "bg-[#345952]/8" : "hover:bg-[#FDFBF2]"
                          }`}
                        >
                          <span
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white text-xs font-bold"
                            style={{ backgroundColor: roleColor(emp.role) }}
                          >
                            {emp.emp_name.charAt(0).toUpperCase()}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-semibold text-[#4A4A48] truncate">
                              {emp.emp_name}
                            </span>
                            <span className="block text-[11px] text-[#8A8A85] truncate">
                              {emp.role}
                              {emp.shift ? ` · ${emp.shift}` : ""}
                            </span>
                          </span>
                          {active ? (
                            <FaCheck className="shrink-0 text-sm" style={{ color: DASHBOARD.header }} />
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </Fragment>
              ))
            )}
          </ul>
        </DropdownPanel>
      ) : null}
    </div>
  );
}

const CASE_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "All types" },
  { value: "live", label: "Live" },
  { value: "redesign", label: "Redesign" },
  { value: "internal_live", label: "Internal live" },
  { value: "internal_redesign", label: "Internal redesign" },
];

export function CaseTypeSelect({
  value,
  onChange,
  variant = "header",
  className = "",
}: {
  value: string;
  onChange: (value: string) => void;
  variant?: Variant;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss(open, close);
  const selected =
    CASE_TYPE_OPTIONS.find((o) => o.value === value) || CASE_TYPE_OPTIONS[0];

  return (
    <div ref={ref} className={`relative min-w-0 ${className}`}>
      <TriggerShell
        variant={variant}
        open={open}
        onClick={() => setOpen((o) => !o)}
        icon={<FaCheck className="text-xs" />}
        label="Case type"
        value={selected.label}
      />
      {open ? (
        <DropdownPanel width="w-full sm:w-[240px]">
          <ul className="max-h-[280px] overflow-y-auto py-1">
            {CASE_TYPE_OPTIONS.map((opt) => {
              const active = opt.value === value;
              return (
                <li key={opt.value || "all"}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      close();
                    }}
                    className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                      active ? "bg-[#345952]/8 font-semibold text-[#345952]" : "text-[#4A4A48] hover:bg-[#FDFBF2]"
                    }`}
                  >
                    {opt.label}
                    {active ? (
                      <FaCheck className="shrink-0 text-sm" style={{ color: DASHBOARD.header }} />
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </DropdownPanel>
      ) : null}
    </div>
  );
}

function CalendarGrid({
  viewMonth,
  selectedIso,
  minIso,
  maxIso,
  onSelect,
}: {
  viewMonth: Date;
  selectedIso: string;
  minIso?: string;
  maxIso?: string;
  onSelect: (iso: string) => void;
}) {
  const selected = parseIsoToDate(selectedIso);
  const min = minIso ? parseIsoToDate(minIso) : null;
  const max = maxIso ? parseIsoToDate(maxIso) : null;

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells: (Date | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const isDisabled = (day: Date) => {
    const t = startOfDay(day);
    if (min && t < startOfDay(min)) return true;
    if (max && t > startOfDay(max)) return true;
    return false;
  };

  return (
    <div className="p-3">
      <div className="grid grid-cols-7 gap-1 mb-2">
        {WEEKDAYS.map((w) => (
          <div
            key={w}
            className="text-center text-[10px] font-semibold uppercase text-[#8A8A85] py-1"
          >
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) => {
          if (!day) return <div key={`e-${i}`} />;
          const disabled = isDisabled(day);
          const active = isSameDay(day, selected);
          const iso = dateToIso(day);
          return (
            <button
              key={iso}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(iso)}
              className={`h-9 rounded-lg text-sm font-medium transition-colors ${
                active
                  ? "text-white shadow-sm"
                  : disabled
                    ? "text-[#D0D0CB] cursor-not-allowed"
                    : "text-[#4A4A48] hover:bg-[#FDFBF2]"
              }`}
              style={active ? { backgroundColor: DASHBOARD.header } : undefined}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function CustomDatePicker({
  value,
  onChange,
  min,
  max,
  label,
  variant = "header",
  className = "",
}: {
  value: string;
  onChange: (iso: string) => void;
  min?: string;
  max?: string;
  label: string;
  variant?: Variant;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => parseIsoToDate(value));
  const close = useCallback(() => setOpen(false), []);
  const panelRef = useRef<HTMLDivElement>(null);
  const ref = useDismiss(open, close, panelRef);

  useEffect(() => {
    if (open) setViewMonth(parseIsoToDate(value));
  }, [open, value]);

  const prevMonth = () =>
    setViewMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () =>
    setViewMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  return (
    <div ref={ref} className={`relative min-w-0 ${className}`}>
      <TriggerShell
        variant={variant}
        open={open}
        onClick={() => setOpen((o) => !o)}
        icon={<FaCalendarAlt className="text-xs" />}
        label={label}
        value={formatDisplayDate(value)}
      />

      <DropdownPortal
        open={open}
        anchorRef={ref}
        panelRef={panelRef}
        estimatedHeight={380}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E8E8E4] bg-[#FDFBF2]">
          <button
            type="button"
            onClick={prevMonth}
            className="h-8 w-8 shrink-0 rounded-lg flex items-center justify-center text-[#4A4A48] hover:bg-white border border-transparent hover:border-[#E8E8E4]"
            aria-label="Previous month"
          >
            <FaChevronLeft className="text-xs" />
          </button>
          <span className="text-sm font-semibold text-[#4A4A48] whitespace-nowrap">
            {MONTHS[viewMonth.getMonth()]} {viewMonth.getFullYear()}
          </span>
          <button
            type="button"
            onClick={nextMonth}
            className="h-8 w-8 shrink-0 rounded-lg flex items-center justify-center text-[#4A4A48] hover:bg-white border border-transparent hover:border-[#E8E8E4]"
            aria-label="Next month"
          >
            <FaChevronRight className="text-xs" />
          </button>
        </div>
        <CalendarGrid
          viewMonth={viewMonth}
          selectedIso={value}
          minIso={min}
          maxIso={max}
          onSelect={(iso) => {
            onChange(iso);
            close();
          }}
        />
        <div className="px-4 py-2 border-t border-[#E8E8E4] bg-[#FDFBF2] flex justify-between items-center gap-3">
          <span className="text-xs text-[#8A8A85]">Selected</span>
          <span className="text-sm font-semibold whitespace-nowrap" style={{ color: DASHBOARD.header }}>
            {formatDisplayDate(value)}
          </span>
        </div>
      </DropdownPortal>
    </div>
  );
}

export type DatePreset = "day" | "week" | "month" | "custom";

export const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  day: "Day",
  week: "Week",
  month: "Month",
  custom: "Custom",
};

export function rangeForPreset(
  preset: Exclude<DatePreset, "custom">,
  dayDate?: string
) {
  const today = dateToIso(new Date());
  if (preset === "day") {
    const d = dayDate || today;
    return { startDate: d, endDate: d };
  }
  const from = new Date();
  from.setDate(from.getDate() - (preset === "week" ? 6 : 29));
  return { startDate: dateToIso(from), endDate: today };
}

export function inferDatePreset(startDate: string, endDate: string): DatePreset {
  if (startDate === endDate) return "day";
  const week = rangeForPreset("week");
  if (startDate === week.startDate && endDate === week.endDate) return "week";
  const month = rangeForPreset("month");
  if (startDate === month.startDate && endDate === month.endDate) return "month";
  return "custom";
}

export function PeriodPresetBar({
  preset,
  onChange,
  variant = "header",
}: {
  preset: DatePreset;
  onChange: (preset: DatePreset) => void;
  variant?: Variant;
}) {
  const isHeader = variant === "header";
  const presets = Object.keys(DATE_PRESET_LABELS) as DatePreset[];

  return (
    <div
      className={`flex rounded-full p-1 w-full sm:w-auto justify-center ${
        isHeader ? "" : "bg-[#F8F8F6]"
      }`}
      style={isHeader ? { backgroundColor: "rgba(255,255,255,0.15)" } : undefined}
    >
      {presets.map((id) => (
        <button
          key={id}
          type="button"
          onClick={() => onChange(id)}
          className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors ${
            preset === id
              ? isHeader
                ? "bg-white text-gray-800 shadow-sm"
                : "bg-white text-[#2fa9bf] shadow-sm"
              : isHeader
                ? "text-white/90 hover:text-white"
                : "text-[#6B6B66] hover:text-[#4A4A48]"
          }`}
        >
          {DATE_PRESET_LABELS[id]}
        </button>
      ))}
    </div>
  );
}

export function DashboardDateRange({
  startDate,
  endDate,
  onStartChange,
  onEndChange,
  variant = "header",
}: {
  startDate: string;
  endDate: string;
  onStartChange: (v: string) => void;
  onEndChange: (v: string) => void;
  variant?: Variant;
}) {
  const isHeader = variant === "header";

  return (
    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto min-w-0">
      <CustomDatePicker
        label="From"
        value={startDate}
        max={endDate}
        onChange={onStartChange}
        variant={variant}
        className="flex-1 sm:flex-none sm:w-[200px] shrink-0"
      />
      <span
        className={`text-xs font-semibold uppercase tracking-wider text-center shrink-0 px-1 ${
          isHeader ? "text-white/50" : "text-[#8A8A85]"
        }`}
      >
        to
      </span>
      <CustomDatePicker
        label="To"
        value={endDate}
        min={startDate}
        onChange={onEndChange}
        variant={variant}
        className="flex-1 sm:flex-none sm:w-[200px] shrink-0"
      />
    </div>
  );
}

export type PeriodSelection = {
  preset: DatePreset;
  startDate: string;
  endDate: string;
};

const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function yearMonthOf(iso: string) {
  const [year, month] = iso.split("-").map(Number);
  return { year, month };
}

function rangeForScope(
  preset: Exclude<DatePreset, "custom">,
  anchor: string,
  year: number,
  month: number,
) {
  if (preset === "day") {
    const day = sameDayInMonth(anchor, year, month);
    return { startDate: day, endDate: day };
  }
  if (preset === "week") {
    const day = sameDayInMonth(anchor, year, month);
    return weekContaining(day, year, month);
  }
  return monthBounds(year, month);
}

export function OverviewPeriodControls({
  preset,
  startDate,
  endDate,
  years,
  variant = "header",
  onChange,
}: {
  preset: DatePreset;
  startDate: string;
  endDate: string;
  years: number[];
  variant?: Variant;
  onChange: (next: PeriodSelection) => void;
}) {
  const isHeader = variant === "header";
  const { year, month } = yearMonthOf(startDate);
  const [monthOpen, setMonthOpen] = useState(false);
  const [weekOpen, setWeekOpen] = useState(false);
  const [dayOpen, setDayOpen] = useState(false);
  const closeMonth = useCallback(() => setMonthOpen(false), []);
  const closeWeek = useCallback(() => setWeekOpen(false), []);
  const closeDay = useCallback(() => setDayOpen(false), []);
  const monthRef = useDismiss(monthOpen, closeMonth);
  const weekRef = useDismiss(weekOpen, closeWeek);
  const dayPanel = useRef<HTMLDivElement>(null);
  const dayAnchor = useDismiss(dayOpen, closeDay, dayPanel);

  const apply = (nextPreset: DatePreset, start: string, end: string) => {
    onChange({ preset: nextPreset, startDate: start, endDate: end });
  };

  const selectPreset = (next: DatePreset) => {
    if (next === "custom") {
      apply("custom", startDate, endDate);
      return;
    }
    const range = rangeForScope(next, startDate, year, month);
    apply(next, range.startDate, range.endDate);
  };

  const shiftScope = (nextYear: number, nextMonth: number) => {
    if (preset === "custom") return;
    const range = rangeForScope(preset, startDate, nextYear, nextMonth);
    apply(preset, range.startDate, range.endDate);
  };

  const weeks = weeksInMonth(year, month);

  return (
    <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 w-full xl:w-auto min-w-0">
      <PeriodPresetBar preset={preset} onChange={selectPreset} variant={variant} />

      {preset === "custom" ? (
        <DashboardDateRange
          startDate={startDate}
          endDate={endDate}
          onStartChange={(value) => {
            if (!value) return;
            const end = endDate < value ? value : endDate;
            apply("custom", value, end);
          }}
          onEndChange={(value) => {
            if (!value) return;
            const start = startDate > value ? value : startDate;
            apply("custom", start, value);
          }}
          variant={variant}
        />
      ) : (
        <>
          <div
            className={`flex rounded-full p-1 w-full sm:w-auto justify-center ${
              isHeader ? "" : "bg-[#F8F8F6]"
            }`}
            style={isHeader ? { backgroundColor: "rgba(255,255,255,0.15)" } : undefined}
          >
            {years.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => shiftScope(item, month)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  year === item
                    ? isHeader
                      ? "bg-white text-gray-800 shadow-sm"
                      : "bg-white text-[#345952] shadow-sm"
                    : isHeader
                      ? "text-white/90 hover:text-white"
                      : "text-[#6B6B66] hover:text-[#4A4A48]"
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          <div ref={monthRef} className="relative min-w-0 w-full sm:w-[150px]">
            <TriggerShell
              variant={variant}
              open={monthOpen}
              onClick={() => setMonthOpen((open) => !open)}
              icon={<FaCalendarAlt className="text-xs" />}
              label="Month"
              value={MONTHS[month - 1]}
            />
            {monthOpen ? (
              <DropdownPanel width="w-full sm:w-[220px]">
                <ul className="max-h-64 overflow-y-auto py-1">
                  {MONTHS.map((name, index) => {
                    const active = index + 1 === month;
                    return (
                      <li key={name}>
                        <button
                          type="button"
                          onClick={() => {
                            shiftScope(year, index + 1);
                            closeMonth();
                          }}
                          className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm ${
                            active ? "bg-[#F4FAF8] font-semibold text-[#345952]" : "text-[#4A4A48] hover:bg-[#FDFBF2]"
                          }`}
                        >
                          {name}
                          {active ? <FaCheck className="text-xs" /> : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </DropdownPanel>
            ) : null}
          </div>

          {preset === "day" ? (
            <div ref={dayAnchor} className="relative min-w-0 w-full sm:w-[200px]">
              <TriggerShell
                variant={variant}
                open={dayOpen}
                onClick={() => setDayOpen((open) => !open)}
                icon={<FaCalendarAlt className="text-xs" />}
                label="Day"
                value={formatDisplayDate(startDate)}
              />
              <DropdownPortal
                open={dayOpen}
                anchorRef={dayAnchor}
                panelRef={dayPanel}
                estimatedHeight={360}
              >
                <div className="flex items-center justify-between border-b border-[#E8E8E4] bg-[#FDFBF2] px-4 py-3">
                  <button
                    type="button"
                    onClick={() =>
                      shiftScope(month === 1 ? year - 1 : year, month === 1 ? 12 : month - 1)
                    }
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-[#4A4A48] hover:bg-white"
                    aria-label="Previous month"
                  >
                    <FaChevronLeft className="text-xs" />
                  </button>
                  <span className="text-sm font-semibold text-[#4A4A48]">
                    {SHORT_MONTHS[month - 1]} {year}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      shiftScope(month === 12 ? year + 1 : year, month === 12 ? 1 : month + 1)
                    }
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-[#4A4A48] hover:bg-white"
                    aria-label="Next month"
                  >
                    <FaChevronRight className="text-xs" />
                  </button>
                </div>
                <CalendarGrid
                  viewMonth={new Date(year, month - 1, 1)}
                  selectedIso={startDate}
                  onSelect={(iso) => {
                    apply("day", iso, iso);
                    closeDay();
                  }}
                />
              </DropdownPortal>
            </div>
          ) : null}

          {preset === "week" ? (
            <div ref={weekRef} className="relative min-w-0 w-full sm:w-[240px]">
              <TriggerShell
                variant={variant}
                open={weekOpen}
                onClick={() => setWeekOpen((open) => !open)}
                icon={<FaCalendarAlt className="text-xs" />}
                label="Week"
                value={`${formatDisplayDate(startDate).slice(0, 5)} – ${formatDisplayDate(endDate).slice(0, 5)}`}
              />
              {weekOpen ? (
                <DropdownPanel width="w-full sm:w-[260px]">
                  <ul className="max-h-64 overflow-y-auto py-1">
                    {weeks.map((week) => {
                      const active = week.startDate === startDate && week.endDate === endDate;
                      return (
                        <li key={week.startDate}>
                          <button
                            type="button"
                            onClick={() => {
                              apply("week", week.startDate, week.endDate);
                              closeWeek();
                            }}
                            className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm ${
                              active
                                ? "bg-[#F4FAF8] font-semibold text-[#345952]"
                                : "text-[#4A4A48] hover:bg-[#FDFBF2]"
                            }`}
                          >
                            {formatDisplayDate(week.startDate)} – {formatDisplayDate(week.endDate)}
                            {active ? <FaCheck className="text-xs" /> : null}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </DropdownPanel>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export function AdminEmployeeList({
  team,
  selectedId,
  onSelect,
  loading = false,
  title = "Select Employee",
  subtitle = "Choose whose dashboard you want to view",
  fill = false,
  grouped = false,
}: {
  team: EmployeePickerItem[] | EmployeeListItem[];
  selectedId: number | null;
  onSelect: (empId: number) => void;
  loading?: boolean;
  title?: string;
  subtitle?: string;
  fill?: boolean;
  grouped?: boolean;
}) {
  const [search, setSearch] = useState("");
  const [showSecondary, setShowSecondary] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matched = q
      ? team.filter(
          (e) =>
            e.emp_name.toLowerCase().includes(q) ||
            e.role.toLowerCase().includes(q) ||
            (e.shift || "").toLowerCase().includes(q)
        )
      : team;
    if (!grouped) return matched;
    if (q) return matched;
    return matched.filter((e) => {
      const group = pickerGroup(e);
      if (group === "primary") return true;
      if (group === "secondary") return showSecondary;
      return false;
    });
  }, [team, search, grouped, showSecondary]);

  const sections = useMemo(() => {
    if (!grouped) return [{ label: null as string | null, rows: filtered }];
    const primary = filtered.filter((e) => pickerGroup(e) === "primary");
    const secondary = filtered.filter((e) => pickerGroup(e) === "secondary");
    const other = filtered.filter((e) => pickerGroup(e) === "other");
    return [
      { label: "Design team", rows: primary },
      { label: "CRM & Admin", rows: secondary },
      { label: "Other", rows: other },
    ].filter((section) => section.rows.length);
  }, [filtered, grouped]);

  const secondaryCount = useMemo(
    () => team.filter((e) => pickerGroup(e) === "secondary").length,
    [team],
  );

  return (
    <div
      className={`bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-[#E8E8E4] min-w-0 ${
        fill ? "flex flex-1 flex-col min-h-[calc(100dvh-10.5rem)]" : ""
      }`}
      style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-sm font-semibold text-[#4A4A48]">{title}</h2>
          <p className="text-xs text-[#8A8A85] mt-0.5">{subtitle}</p>
        </div>
        <div className="relative w-full sm:max-w-xs">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A85] text-xs" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or role…"
            className="w-full rounded-xl border border-[#E8E8E4] bg-[#FDFBF2] py-2.5 pl-9 pr-3 text-sm text-[#4A4A48] outline-none focus:border-[#93B9AD]"
          />
        </div>
      </div>

      {loading ? (
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 ${
            fill ? "flex-1 content-start" : ""
          }`}
        >
          {Array.from({ length: fill ? 12 : 6 }).map((_, i) => (
            <div key={i} className="h-16 rounded-xl bg-[#F4F4F2] animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 && !(grouped && !showSecondary && secondaryCount) ? (
        <p className="text-sm text-[#8A8A85] py-6 text-center">No employees match your search.</p>
      ) : (
        <div
          className={`overflow-x-hidden pr-1 ${
            fill ? "flex-1 min-h-0 overflow-y-auto" : ""
          }`}
        >
          {sections.map((section) => (
            <div key={section.label || "all"} className="mb-3 last:mb-0">
              {section.label ? (
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8A8A85]">
                  {section.label}
                </p>
              ) : null}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 content-start">
                {section.rows.map((emp) => {
                  const active = emp.emp_id === selectedId;
                  return (
                    <button
                      key={emp.emp_id}
                      type="button"
                      onClick={() => onSelect(emp.emp_id)}
                      className={`text-left rounded-xl px-3 py-3 border transition-all min-w-0 ${
                        active
                          ? "border-[#345952] bg-[#345952]/5 ring-1 ring-[#345952]/20"
                          : "border-[#E8E8E4] bg-[#FDFBF2] hover:border-[#93B9AD]/60 hover:bg-white"
                      }`}
                    >
                      <div className="flex items-start gap-2 min-w-0">
                        <span
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white text-xs font-bold"
                          style={{ backgroundColor: roleColor(emp.role) }}
                        >
                          {emp.emp_name.charAt(0).toUpperCase()}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-[#4A4A48] truncate">{emp.emp_name}</p>
                          <p className="text-[11px] text-[#8A8A85] truncate">
                            {emp.role}
                            {emp.shift ? ` · ${emp.shift}` : ""}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {grouped && !search.trim() && secondaryCount > 0 ? (
            <button
              type="button"
              onClick={() => setShowSecondary((v) => !v)}
              className="mt-1 text-xs font-medium text-[#345952] hover:underline"
            >
              {showSecondary
                ? "Hide CRM & Admin"
                : `Show CRM & Admin (${secondaryCount})`}
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

export function DashboardSearchInput({
  value,
  onChange,
  placeholder,
  className = "",
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  className?: string;
}) {
  return (
    <div className={`relative min-w-0 ${className}`}>
      <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A85] text-xs" />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-[#E8E8E4] bg-white py-2.5 pl-9 pr-3 text-sm text-[#4A4A48] outline-none focus:border-[#93B9AD] shadow-sm"
      />
    </div>
  );
}

export function DashboardPagination({
  page,
  totalPages,
  onPrev,
  onNext,
}: {
  page: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <div className="flex items-center gap-2 shrink-0">
      <button
        type="button"
        disabled={page <= 1}
        onClick={onPrev}
        className="px-3 py-2 rounded-xl text-sm font-medium border border-[#E8E8E4] bg-white text-[#4A4A48] hover:border-[#93B9AD] disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Prev
      </button>
      <span className="text-sm text-[#8A8A85] whitespace-nowrap">
        Page {totalPages === 0 ? 0 : page} of {totalPages}
      </span>
      <button
        type="button"
        disabled={page >= totalPages}
        onClick={onNext}
        className="px-3 py-2 rounded-xl text-sm font-medium border border-[#E8E8E4] bg-white text-[#4A4A48] hover:border-[#93B9AD] disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Next
      </button>
    </div>
  );
}

type SelectOption = { value: string; label: string };

export function CustomSimpleSelect({
  value,
  onChange,
  options,
  label,
  placeholder,
  className = "",
}: {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  label: string;
  placeholder?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss(open, close);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} className={`relative min-w-0 ${className}`}>
      <TriggerShell
        variant="light"
        open={open}
        onClick={() => setOpen((o) => !o)}
        icon={<FaChevronDown className="text-[10px]" />}
        label={label}
        value={selected?.label ?? placeholder ?? "Select…"}
      />
      {open ? (
        <DropdownPanel width="w-full sm:min-w-[200px]">
          <ul className="max-h-[240px] overflow-y-auto py-1">
            {options.map((opt) => {
              const active = opt.value === value;
              return (
                <li key={opt.value}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      close();
                    }}
                    className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left text-sm transition-colors ${
                      active ? "bg-[#345952]/8 text-[#345952] font-semibold" : "text-[#4A4A48] hover:bg-[#FDFBF2]"
                    }`}
                  >
                    <span>{opt.label}</span>
                    {active ? <FaCheck className="text-xs shrink-0" /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </DropdownPanel>
      ) : null}
    </div>
  );
}

export function DashboardTableCard({
  children,
  title,
}: {
  children: ReactNode;
  title?: string;
}) {
  return (
    <div
      className="bg-white rounded-2xl border border-[#E8E8E4] overflow-hidden min-w-0 shadow-sm"
      style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}
    >
      {title ? (
        <div className="px-5 py-4 border-b border-[#E8E8E4] bg-[#FDFBF2]">
          <h2 className="text-sm font-semibold text-[#4A4A48]">{title}</h2>
        </div>
      ) : null}
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

export const portalTableHead =
  "py-3 px-4 sm:px-6 text-start text-[10px] uppercase tracking-wider font-semibold text-[#8A8A85]";

export const portalTableCell = "py-3 px-4 sm:px-6 text-[#4A4A48]";
